/* eslint id-length: [2, {"exceptions":["a","b","i"]}] */
import mori from 'mori';
import buildMapFromTimeframe from './mapData';
import buildGraphDataFromTimeframes from './linechartData';
import buildTableData from './tableData';
import getCombinedFilters from './getCombinedFilters';
import getSelectedDistributions from './getSelectedDistributions';
import getFootnotes from './getFootnotes/';
import footnoteCallback from './getFootnotes/footnoteCallback';
import {
  filterByTimeframe,
  filterMetaDataByTimeframe,
} from './dataFromOptions';

let cachedOptions = {};
let footnoteHash = null;

export default function(sheetData, render, options = {}, reduxCallback) {
  if (!render.table) {
    render.table = {};
  }

  const baseContext = mori.getIn(
    sheetData.config,
    ['globalRules', 'Context'],
    []
  )[0] || false;

  // @TODO - add a check/default for options.selectedRows.  Main reason
  // being that we are checking if it exists || {} in a number of places.

  const filteredData = filterByTimeframe(sheetData, options);
  const filteredMetaData = filterMetaDataByTimeframe(sheetData, options);
  const combinedFilters =
    getCombinedFilters(sheetData, options, filteredMetaData);
  const isMultiGeo = mori.get(combinedFilters[4], [1], false);

  // Gets the first key in the selected rows tree
  const treeContextSlug = Object.keys(options.selectedRows || {})[0] || false;

  const footnotes = getFootnotes(
    sheetData,
    filteredMetaData,
    options,
    filteredData,
    combinedFilters
  );

  function footnoteCb(key) {
    return footnoteCallback(
      key,
      footnotes.labeledFootnotes,
      !!(options.currentTimeframe && options.startTimeframe)
    );
  }

  buildTableData.bind(render.table)(
    sheetData,
    filteredMetaData,
    options,
    filteredData,
    [
      mori.set(), // All columns
      mori.concat(
        mori.vector(combinedFilters[4][0]),
        getSelectedDistributions(
          sheetData,
          filteredMetaData,
          filteredMetaData.immutableDistributions,
          sheetData.globalSettings.hasDataviews &&
            !mori.getIn(sheetData.config, ['globalRules', 'Locked'], false) ?
              mori.nth(
                filteredMetaData.immutableDataviews,
                +(options.dataView || 0)
              ) :
              false
        )),
      filteredMetaData.immutableDistributions,
      combinedFilters[3],
      combinedFilters[4],
    ],
    () => { // Ready callback
      // This is added to the API so we can access it when building CSV data
      render.table.api.baseColumnContext = baseContext;
      render.table.api.isMultiGeo = isMultiGeo;
      render.table.api.footnoteCallback = footnoteCb;

      render.table.filterDistributions(
        combinedFilters[1],
        options,
        mori.intoArray(mori.map((item) => mori.first(item), filteredData))
      );
      render.table.filterData(!isMultiGeo ? combinedFilters[0] : null);
      render.table.api.setSortModel([options.sortModel || {
        colId: isMultiGeo ? baseContext : 'Location',
        sort: 'asc',
      }]);
    },
    combinedFilters[0], // Selected Keys
    isMultiGeo,
    reduxCallback,
    // Callback general post X actions
    (table) => {
      table.filterDistributions(
        combinedFilters[1],
        options,
        mori.intoArray(mori.map((item) => mori.first(item), filteredData)),
      );

      table.filterData(
        !isMultiGeo ? combinedFilters[0] : null,
      );

      if (table.api && options.activeTab === 'graph') {
        table.api.setSortModel([options.sortModel || {
          colId: isMultiGeo ? baseContext : 'Location',
          sort: 'asc',
        }]);
      }
    }
  );

  if (footnoteHash !== mori.hash(footnotes.labeledFootnotes) &&
    render.table.api
  ) {
    render.table.api.footnoteCallback = footnoteCb;
    footnoteHash = mori.hash(footnotes.labeledFootnotes);
    render.table.api.refreshHeader();
    render.table.api.refreshView();
    if (render.table.onAfterFilterChanged) {
      render.table.onAfterFilterChanged(); // We need to fire this to reset headers
    }
  }

  render.footnotes = (mori.toJs(footnotes.filteredFootnotes) || [])
    .reduce((acc, curr) => {
      if (acc.filter(({ id }) => id === curr.id).length) {
        return acc;
      }
      acc.push(curr);
      return acc;
    }, [])
    .sort(({ weight: a }, { weight: b }) => a - b);

  // We want to build the map data if we are on the map tab
  // and certain options have changed
  if (options.activeTab === 'map' &&
    (typeof render.mapData === 'undefined' || // check that map has rendered at least once
      isMultiGeo || // force map refresh if multigeo as selection could have changed
      options.dataView !== cachedOptions.dataView ||
      options.selectedDistributions !== cachedOptions.selectedDistributions ||
      options.currentTimeframe !== cachedOptions.currentTimeframe
    )
  ) {
    render.mapData = buildMapFromTimeframe(
      sheetData,
      filteredMetaData,
      options,
      treeContextSlug,
      filteredData,
      ...combinedFilters
    );
  } else if (options.activeTab === 'graph') {
    render.graph = buildGraphDataFromTimeframes(
      sheetData,
      options,
      filteredData,
      ...combinedFilters
    );
    // can't use ng-if on an immutable
    render.graphHasData = !mori.isEmpty(render.graph);
  }

  render.notes = renderNotes(
    sheetData.config,
    sheetData.timeframes,
    options
  );

  cachedOptions = Object.assign({}, options);

  // From getSelectedColumns:  If the selected rows is invalid,
  // we need to reset the selectedRows tree in the URL
  const validSelection = treeContextSlug !== 'nested' && (
    !!sheetData.columns[treeContextSlug] ||
    !!sheetData.columns.nested[treeContextSlug]
  );

  if (!validSelection) {
    reduxCallback({ selectedRows: undefined });
    return;
  }
}

/**
 * Given the confige, timeframes, and options, find which notes should be shown
 *
 * @param  {object} config        Datasheet config
 * @param  {array} allTimeframes  Datasheet timeframes
 * @param  {object} options       Options object
 * @return {array}                Array of notes to show
 */
export function renderNotes(config, allTimeframes, options) {
  return mori.toJs(
    mori.map(
      (item) => mori.hashMap(
        'name', mori.first(item),
        'val', mori.filter(
          (val) => mori.count(mori.intersection(
            mori.conj(
              mori.set(allTimeframes.slice(
                +options.currentTimeframe || 0,
                (+options.currentTimeframe || 0) + 1
              ).map((curr) => curr.replace(/\s+/g, ''))),
              'any',
              ''
            ),
            mori.set(
              (mori.get(val, 'Timeframe', '') || '')
                .replace(/\s+/g, '')
                .split(',')
              )
          )) > 0,
          mori.last(item)
        )
      ),
      mori.get(config, 'notesSources')
    )
  );
}



/** WEBPACK FOOTER **
 ** ../~/eslint-loader!./js/services/renderData.js
 **/